/**
 * compactDdlNonTableArms — the per-objectKind bodies for every DDL kind that
 * is NOT the table family, split out of `compactDdlBodyArm.tsx` to keep that
 * dispatcher small.
 *
 * Each body is the `InlineDdl<Kind>Body` export from the corresponding
 * `inline/InlineDdl<Kind>Body.tsx` — the SAME form, the SAME `testId`
 * strings, the same parked-statement scan and engine gating, mounted
 * directly with no trigger/Popover chrome (the always-on inline toolbar
 * that used to host these behind a `[Edit …]` button was removed in T5).
 *
 * Conventions the Body contract tables fix:
 *  - `parsedOverride` is a TEST-ONLY seam — never passed here, so every body
 *    performs its own live scan off `editContext`.
 *  - `onDone` is optional and fires on BOTH a successful commit and Cancel.
 *    The compact popover deliberately does NOT pass it: a commit funnels
 *    through the app-shell PreviewDialog, which needs this arm's live context
 *    to stay mounted, so the popover must survive it.
 *  - `onCancel` is the cancel-ONLY half, threaded from `CompactDdlArmProps`.
 *    The popover passes it so its pinned [Cancel] closes the popup while a
 *    commit still leaves it open. Bodies fall back to `onDone` when it is
 *    omitted, so every other host keeps the old behaviour.
 *  - Engine-unsupported cases render each body's own muted
 *    `inline-ddl-<kind>-unsupported` notice (or the `*FormBody`'s internal
 *    `<testId>-unsupported`), never a disabled trigger.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3 wiring
 * of the T4a/T4b bodies; T5 removed the legacy toolbar host).
 */
import type { ReactNode } from 'react';

import type { DdlObjectKind } from '../edit/editContext';
import { InlineDdlDatabaseBody } from '../inline/InlineDdlDatabaseBody';
import { InlineDdlGrantBody } from '../inline/InlineDdlGrantBody';
import { InlineDdlMatViewRefreshBody } from '../inline/InlineDdlMatViewRefreshBody';
import { InlineDdlPackageBody } from '../inline/InlineDdlPackageBody';
import { InlineDdlRoutineBody } from '../inline/InlineDdlRoutineBody';
import { InlineDdlSchemaBody } from '../inline/InlineDdlSchemaBody';
import { InlineDdlSecurityBody } from '../inline/InlineDdlSecurityBody';
import { InlineDdlSequenceBody } from '../inline/InlineDdlSequenceBody';
import { InlineDdlTriggerBody } from '../inline/InlineDdlTriggerBody';
import { InlineDdlTypeBody } from '../inline/InlineDdlTypeBody';
import { InlineDdlViewBodyBody } from '../inline/InlineDdlViewBodyBody';
import type { CompactDdlArmProps } from './compactDdlBodyArm';

/** GRANT/REVOKE share the `security` objectKind with CREATE/ALTER
 *  LOGIN|USER|ROLE, so the security arm branches on `ddlKind`. No other arm
 *  needs `ddlKind`. */
const GRANT_DDL_KINDS: ReadonlySet<string> = new Set(['grant', 'revoke']);

export function renderCompactDdlNonTableArm(
  objectKind: DdlObjectKind,
  { editContext, ddlKind, onCancel }: CompactDdlArmProps,
): ReactNode | null {
  switch (objectKind) {
    case 'view':
      return <InlineDdlViewBodyBody editContext={editContext} onCancel={onCancel} />;
    case 'materialized-view':
      // Body + the refresh action stacked: a parked CREATE MATERIALIZED VIEW
      // scans identically to a view, and refreshing it is a non-buffer action
      // dispatched against the live connection. Only the view form claims the
      // popover's pinned action row (via `FormActionsSlot`) — and with it the
      // host's `onCancel`; the refresh panel keeps its own buttons (and its own
      // panel-scoped Cancel) and is boxed so they read as ITS actions rather
      // than a second, competing bottom row.
      return (
        <div className="flex flex-col gap-3">
          <InlineDdlViewBodyBody editContext={editContext} onCancel={onCancel} />
          <div className="rounded-md border border-border p-2">
            <InlineDdlMatViewRefreshBody editContext={editContext} />
          </div>
        </div>
      );
    case 'sequence':
      return <InlineDdlSequenceBody editContext={editContext} onCancel={onCancel} />;
    case 'type':
      return (
        <InlineDdlTypeBody editContext={editContext} objectKind="type" onCancel={onCancel} />
      );
    case 'domain':
      return (
        <InlineDdlTypeBody editContext={editContext} objectKind="domain" onCancel={onCancel} />
      );
    case 'schema':
      return <InlineDdlSchemaBody editContext={editContext} onCancel={onCancel} />;
    case 'trigger':
      return <InlineDdlTriggerBody editContext={editContext} onCancel={onCancel} />;
    case 'routine':
      return <InlineDdlRoutineBody editContext={editContext} onCancel={onCancel} />;
    case 'package':
      return <InlineDdlPackageBody editContext={editContext} onCancel={onCancel} />;
    case 'security':
      return ddlKind && GRANT_DDL_KINDS.has(ddlKind) ? (
        <InlineDdlGrantBody editContext={editContext} onCancel={onCancel} />
      ) : (
        <InlineDdlSecurityBody editContext={editContext} onCancel={onCancel} />
      );
    case 'database':
      return <InlineDdlDatabaseBody editContext={editContext} onCancel={onCancel} />;
    default:
      // `index` has no standalone body: index add/edit for the table family
      // lives in the compact TableEditor's Indexes tab, and a bare
      // CREATE INDEX statement has no dedicated inline form to embed. It
      // degrades to the read-only card.
      return null;
  }
}
